/**
 * „Meine Verkäufe"-Tab (Jun-2026, User-Wunsch).
 *
 * Zeigt alle Fahrzeugverkäufe, bei denen der eingeloggte Nutzer als Verkäufer
 * (dispo_salesperson / assigned_seller_id) hinterlegt ist. Mit Suchfeld;
 * Klick auf einen Treffer führt direkt in die Disposition des Fahrzeugs,
 * wo der Verkaufsprozess einsehbar und bearbeitbar ist.
 *
 * Datenquelle: ``GET /api/dealer/my-sales?q=...`` → { items, count }.
 */
import { useEffect, useState, useCallback } from "react";
import { Search, Loader2, X, Wrench, Truck, Car, ArrowDownAZ, Undo2, Handshake } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import DispositionVehicleCard from "../dealer/DispositionVehicleCard";
import MyWorkshopOrdersTab from "./MyWorkshopOrdersTab";
import MyOpenTransportsTab from "./MyOpenTransportsTab";
import MyB2BDealsSection from "./MyB2BDealsSection";

export default function MySalesTab() {
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("all");
    const [actionOnly, setActionOnly] = useState(false);
    const [items, setItems] = useState(null);
    const [loading, setLoading] = useState(false);
    // Jul-2026 (User-Wunsch) — Sortierung + integrierte Bereiche Werkstatt/Transporte
    const [sortOrder, setSortOrder] = useState("newest");
    const [section, setSection] = useState("sales");
    // Jun-2026 (User-Wunsch) — standardmäßig AUCH zugeordnete (noch nicht
    // verkaufte) Fahrzeuge anzeigen; umschaltbar auf „nur echte Verkäufe".
    const [showAll, setShowAll] = useState(true);
    const [showCompleted, setShowCompleted] = useState(false);
    const [hiddenCompleted, setHiddenCompleted] = useState(0);
    const [salesCount, setSalesCount] = useState(null);

    // Jun-2026 (User-Wunsch) — Fahrzeug von Verkauf/Reservierung wieder freigeben
    const releaseSale = async (it) => {
        const reason = window.prompt(
            `„${it.title}" vom Verkauf/der Reservierung freigeben?\n\nBitte Grund angeben (Pflicht — wird im Fahrzeug protokolliert):`);
        if (reason === null) return;
        if (!reason.trim()) { toast.error("Freigabe abgebrochen — ein Grund ist Pflicht"); return; }
        try {
            await api.post(`/listings/${it.id}/dispo/cancel-contract`, { reason: reason.trim() });
            toast.success("Fahrzeug wieder freigegeben — es kann anderweitig verkauft werden");
            load(q, showAll, showCompleted);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Freigabe nicht möglich");
        }
    };

    const load = useCallback(async (query, all = false, withCompleted = false) => {
        setLoading(true);
        try {
            const r = await api.get("/dealer/my-sales", { params: { q: query || "", only_sales: !all, include_completed: withCompleted } });
            setItems(r.data?.items || []);
            setSalesCount(r.data?.sales_count ?? null);
            setHiddenCompleted(r.data?.hidden_completed || 0);
        } catch {
            setItems([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load("", showAll, showCompleted); }, [load, showAll, showCompleted]);

    const openDispo = (it) => window.open(`/edit-listing/${it.id}?focus=disposition`, "_blank", "noopener");

    // Debounced Suche
    useEffect(() => {
        const t = setTimeout(() => load(q, showAll, showCompleted), 300);
        return () => clearTimeout(t);
    }, [q, load, showAll, showCompleted]);

    const counts = {
        all: items?.length || 0,
        reserviert: (items || []).filter((i) => i.status_label === "reserviert").length,
        "vorläufig verkauft": (items || []).filter((i) => i.status_label === "vorläufig verkauft").length,
        verkauft: (items || []).filter((i) => i.status_label === "verkauft").length,
        zulassung: (items || []).filter((i) => i.in_zulassung).length,
        transport: (items || []).filter((i) => i.in_transport).length,
        ausgeliefert: (items || []).filter((i) => i.ausgeliefert).length,
    };
    const matchesStatus = (i) => {
        if (statusFilter === "all") return true;
        if (statusFilter === "zulassung") return !!i.in_zulassung;
        if (statusFilter === "transport") return !!i.in_transport;
        if (statusFilter === "ausgeliefert") return !!i.ausgeliefert;
        return i.status_label === statusFilter;
    };
    const filtered = (items || [])
        .filter((i) => matchesStatus(i) && (!actionOnly || !/abgeschlossen|verkauft/i.test(i.next_step || "")))
        .sort((a, b) => {
            const da = new Date(a.created_at || a.updated_at || 0).getTime();
            const dbb = new Date(b.created_at || b.updated_at || 0).getTime();
            return sortOrder === "oldest" ? da - dbb : dbb - da;
        });
    const STATUS_CHIPS = [
        { key: "all", label: "Alle" },
        { key: "reserviert", label: "Reserviert" },
        { key: "vorläufig verkauft", label: "Vorläufig verkauft" },
        { key: "verkauft", label: "Verkauft" },
        { key: "zulassung", label: "In Zulassung" },
        { key: "transport", label: "In Transport" },
        { key: "ausgeliefert", label: "Ausgeliefert" },
    ];
    const SECTIONS = [
        { key: "sales", label: "Verkäufe", icon: Car },
        { key: "b2b", label: "B2B & Provision", icon: Handshake },
        { key: "workshop", label: "Werkstattaufträge", icon: Wrench },
        { key: "transports", label: "Offene Transporte", icon: Truck },
    ];

    return (
        <div className="bg-white border border-gray-200 rounded-sm p-4" data-testid="my-sales-tab">
            {/* Jul-2026 (User-Wunsch) — Werkstattaufträge & Transporte hier integriert */}
            <div className="flex items-center gap-1.5 mb-4 flex-wrap border-b border-gray-100 pb-3" data-testid="my-sales-sections">
                {SECTIONS.map((s) => (
                    <button key={s.key} type="button" onClick={() => setSection(s.key)}
                            aria-pressed={section === s.key}
                            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border text-xs font-bold transition-colors ${
                                section === s.key
                                    ? "bg-[#111827] text-white border-[#111827]"
                                    : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"
                            }`}
                            data-testid={`my-sales-section-${s.key}`}>
                        <s.icon className="w-3.5 h-3.5" /> {s.label}
                    </button>
                ))}
            </div>
            {section === "workshop" && <MyWorkshopOrdersTab />}
            {section === "transports" && <MyOpenTransportsTab />}
            {section === "b2b" && <MyB2BDealsSection />}
            {section === "sales" && (<>
            <div className="relative mb-3">
                <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                <input
                    type="text"
                    value={q}
                    onChange={(e) => setQ(e.target.value)}
                    placeholder="Suche nach Marke, Modell, FIN, Kennzeichen, Kunde …"
                    className="w-full h-10 pl-9 pr-9 border border-gray-300 rounded-sm text-sm focus:outline-none focus:border-[#111827]"
                    data-testid="my-sales-search"
                />
                {q && (
                    <button
                        type="button"
                        onClick={() => setQ("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700"
                        data-testid="my-sales-search-clear"
                        aria-label="Suche zurücksetzen"
                    >
                        <X className="w-4 h-4" />
                    </button>
                )}
            </div>

            {/* Aug-2026 (User-Wunsch „Nacht-Regel") — Hinweis auf ausgeblendete abgeschlossene Verkäufe */}
            {(hiddenCompleted > 0 || showCompleted) && (
                <div className="flex items-center gap-2 mb-3 text-[11px] text-gray-500 bg-gray-50 border border-gray-200 rounded-sm px-2.5 py-1.5" data-testid="my-sales-hidden-hint">
                    <span>
                        {showCompleted
                            ? "Abgeschlossene Verkäufe werden angezeigt."
                            : `${hiddenCompleted} abgeschlossene${hiddenCompleted === 1 ? "r Verkauf" : " Verkäufe"} ausgeblendet (ausgeliefert = am Folgetag weg, sonst 30 Tage nach Verkauf).`}
                    </span>
                    <button type="button" onClick={() => setShowCompleted((v) => !v)}
                        className="font-bold underline underline-offset-2 hover:text-[#111827] ml-auto shrink-0"
                        data-testid="my-sales-toggle-completed">
                        {showCompleted ? "Wieder ausblenden" : "Anzeigen"}
                    </button>
                </div>
            )}

            {/* Status-Schnellfilter */}
            <div className="flex items-center gap-1.5 mb-3 flex-wrap" data-testid="my-sales-status-filters">
                {STATUS_CHIPS.map((c) => (
                    <button
                        key={c.key}
                        type="button"
                        onClick={() => setStatusFilter(c.key)}
                        aria-pressed={statusFilter === c.key}
                        className={`px-2.5 py-1 rounded-sm border text-xs font-bold transition-colors ${
                            statusFilter === c.key
                                ? "bg-[#111827] text-white border-[#111827]"
                                : "bg-white text-gray-600 border-gray-300 hover:border-[#111827]"
                        }`}
                        data-testid={`my-sales-filter-${c.key}`}
                    >
                        {c.label}
                        <span className="ml-1 font-mono-num opacity-70">{counts[c.key] ?? 0}</span>
                    </button>
                ))}
                {salesCount != null && (
                    <span className="ml-auto text-xs font-bold text-gray-700 bg-emerald-50 border border-emerald-200 rounded-sm px-2 py-1" title="Verkauft = reserviert (bis Ablauf), digital signiert oder unterschriebener Kaufvertrag hochgeladen" data-testid="my-sales-sales-count">
                        Verkäufe: {salesCount}
                    </span>
                )}
                <button
                    type="button"
                    onClick={() => setActionOnly((v) => !v)}
                    aria-pressed={actionOnly}
                    className={`px-2.5 py-1 rounded-sm border text-xs font-bold transition-colors ml-1 ${
                        actionOnly
                            ? "bg-orange-500 text-white border-orange-500"
                            : "bg-white text-orange-700 border-orange-300 hover:border-orange-500"
                    }`}
                    data-testid="my-sales-filter-action"
                    title="Nur Verkäufe mit offenen Schritten anzeigen"
                >
                    👉 Handlungsbedarf
                </button>
                <button
                    type="button"
                    onClick={() => setSortOrder((v) => (v === "newest" ? "oldest" : "newest"))}
                    className="inline-flex items-center gap-1 px-2.5 py-1 rounded-sm border border-gray-300 text-xs font-bold text-gray-600 hover:border-[#111827] transition-colors ml-auto"
                    data-testid="my-sales-sort-toggle"
                    title="Sortierung umschalten"
                >
                    <ArrowDownAZ className="w-3.5 h-3.5" />
                    {sortOrder === "newest" ? "Neueste zuerst" : "Älteste zuerst"}
                </button>
            </div>

            {loading && items === null && (
                <div className="text-center py-10 text-gray-500" data-testid="my-sales-loading">
                    <Loader2 className="w-6 h-6 mx-auto animate-spin" />
                </div>
            )}

            {items && filtered.length === 0 && (
                <div className="text-center py-10 text-gray-500 text-sm" data-testid="my-sales-empty">
                    {q || statusFilter !== "all"
                        ? "Keine Verkäufe gefunden — versuchen Sie einen anderen Suchbegriff oder Filter."
                        : (<>
                            <div>{showAll ? "Ihnen sind aktuell keine Fahrzeuge als Verkäufer zugeordnet." : "Ihnen sind aktuell keine Verkäufe zugeordnet."}</div>
                            <div className="text-xs text-gray-400 mt-1">Hier erscheinen Fahrzeuge, bei denen Sie als Verkäufer hinterlegt sind{showAll ? "." : (<>, sobald sie <b>reserviert, verkauft oder in Abwicklung</b> (Kaufvertrag erstellt) sind.</>)}</div>
                            {!showAll && (
                                <button type="button" onClick={() => setShowAll(true)}
                                    className="mt-3 inline-flex items-center gap-1.5 px-3 py-1.5 rounded-sm border border-[#111827] text-xs font-bold text-[#111827] hover:bg-[#111827] hover:text-white transition-colors"
                                    data-testid="my-sales-show-all-btn">
                                    Auch zugeordnete (noch nicht verkaufte) Fahrzeuge anzeigen
                                </button>
                            )}
                        </>)}
                </div>
            )}
            {items && filtered.length > 0 && (
                <div className="mb-2 flex items-center justify-end text-[11px] text-gray-500" data-testid="my-sales-show-all-hint">
                    {showAll ? (
                        <button type="button" onClick={() => setShowAll(false)} className="font-bold underline underline-offset-2 hover:text-[#111827]" data-testid="my-sales-show-sales-only-btn">Nur echte Verkäufe anzeigen</button>
                    ) : (
                        <button type="button" onClick={() => setShowAll(true)} className="font-bold underline underline-offset-2 hover:text-[#111827]" data-testid="my-sales-show-all-btn2">Auch zugeordnete Fahrzeuge anzeigen</button>
                    )}
                </div>
            )}

            {items && filtered.length > 0 && (
                <div className="space-y-2" data-testid="my-sales-list">
                    {filtered.map((it) => {
                        const releasable = it.sold || ["reserviert", "verkauft", "in zulassung", "in transport"].includes((it.status_label || "").toLowerCase());
                        return (
                        <div key={it.id}>
                            <DispositionVehicleCard
                                item={it}
                                selected={false}
                                onToggleSelect={() => {}}
                                onLadenhueterAction={openDispo}
                                onCreateOffer={openDispo}
                                onTestDrive={openDispo}
                                onCreateTransport={openDispo}
                                onCreateDelivery={openDispo}
                            />
                            {releasable && (
                                <div className="flex justify-end -mt-1 mb-1">
                                    <button type="button" onClick={() => releaseSale(it)}
                                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-b-sm border border-t-0 border-red-200 bg-red-50 text-red-700 text-[11px] font-bold hover:bg-red-100 transition-colors"
                                        title="Reservierung/Verkauf stornieren — Fahrzeug wird wieder verfügbar und kann anderweitig verkauft werden (Grund wird protokolliert; gesperrt sobald eine Rechnung existiert)"
                                        data-testid={`my-sales-release-${it.id}`}>
                                        <Undo2 className="w-3 h-3" /> Vom Verkauf freigeben
                                    </button>
                                </div>
                            )}
                        </div>
                        );
                    })}
                </div>
            )}
            </>)}
        </div>
    );
}
